<template>
  <el-dialog
    v-model="visible"
    class="question-guide-dialog"
    :width="hasTabs ? '980px' : '720px'"
    align-center
    :show-close="false"
    destroy-on-close
  >
    <!-- 纯展示模式保留原标题栏 -->
    <template v-if="!hasTabs" #header>
      <span class="dialog-title">{{ title }}</span>
    </template>

    <!-- 右上角关闭按钮：悬浮于弹窗边角外侧（样式对齐周年庆弹窗，依赖 el-dialog 的 overflow: visible） -->
    <button class="dialog-close-btn" aria-label="关闭" @click="handleClose">
      <SvgIcon name="icon-close" :size="16" color="#ffffff" />
    </button>

    <!-- tabs 模式：左侧题型总览 + 右侧规则技巧 双栏布局 -->
    <div class="guide-layout">
      <!-- 左侧：题库综合指南（题量/命中率/重要程度接口暂未提供，展示假数据） -->
      <aside v-if="hasTabs" class="type-panel">
        <div
          class="panel-title"
          :class="{ active: currentTermId === 9001 }"
          @click="switchGuideTitle"
        >
          题库综合指南
          <span class="arrow">›</span>
        </div>
        <span class="new-badge">NEW</span>

        <div class="table-head">
          <span class="col-type">题型</span>
          <span class="col-info">题量 / 稳定命中率 / 重要程度</span>
        </div>

        <div class="table-body" ref="tableBodyRef">
          <div v-for="group in tabGroups" :key="group.category" class="type-group">
            <div class="group-label">{{ group.category }}</div>
            <div class="group-rows">
              <div
                v-for="item in group.list"
                :key="item.termId"
                class="type-row"
                :data-term-id="item.termId"
                :class="{ active: item.termId === currentTermId, 'other-row': isOtherRow(item) }"
                v-yt-push="{ btnName: '题型引导-题型切换' }"
                @click="switchType(item)"
              >
                <!-- 其他听力小题：整行居中展示 -->
                <template v-if="isOtherRow(item)">
                  <span class="other-text">{{ OTHER_LISTEN_LABEL }}</span>
                </template>
                <template v-else>
                  <span class="t-name">{{ item.label }}</span>
                  <span class="t-info">
                    <span class="t-count">{{ typeStat(item).count }}</span>
                    <span class="t-rate">{{
                      typeStat(item).rate ? `(${typeStat(item).rate})` : ''
                    }}</span>
                    <a v-if="typeStat(item).action" class="row-action">{{
                      typeStat(item).action
                    }}</a>
                  </span>
                  <span v-if="typeStat(item).tag" class="row-tag">{{ typeStat(item).tag }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- 右侧：考试规则技巧 -->
      <div class="guide-panel">
        <div v-if="hasTabs" class="guide-header">
          <!-- 数据就绪前不渲染标题气泡，避免接口未返回时闪现兜底标题 -->
          <div v-show="contentReady" class="title-bubble">
            <span class="title-text">
              <!-- 题库综合指南：仅模式名（如「列表」）高亮，后缀「模式指南」保持原色 -->
              <template v-if="currentTermId === 9001 && modeGuides.length">
                <span class="mode-name">{{ modeNameOf(modeGuides[0].title) }}</span
                >模式指南
              </template>
              <template v-else>{{ bubbleTitle }}</template>
            </span>
          </div>
          <img class="eye-icon" src="/image/home/frequencyPanel/yanjin.png" alt="" />
        </div>

        <div class="guide-content">
          <!-- 数据就绪前展示骨架屏（按真实内容结构：小节标题 + 正文行），不闪现兜底文案 -->
          <template v-if="contentReady">
            <!-- 题库综合指南：按模式（列表模式/速记模式）渲染 modeGuides -->
            <template v-if="modeGuides.length">
              <template v-for="(mode, mi) in modeGuides" :key="mode.key">
                <!-- 非第一个模式：内容流中的标题气泡（靠右，NEW 角标叠在气泡右上角） -->
                <div v-if="mi > 0" class="mode-bubble-row">
                  <span class="mode-bubble">
                    <span class="mode-text"
                      ><span class="mode-name">{{ modeNameOf(mode.title) }}</span
                      >模式指南</span
                    >
                    <span v-if="mode.badge" class="mode-badge">{{ mode.badge }}</span>
                  </span>
                </div>
                <section
                  v-for="(sec, si) in mode.sections"
                  :key="`${mode.key}-${si}`"
                  class="guide-section"
                >
                  <h3 class="section-title">{{ sec.title }}</h3>
                  <template v-if="sec.type === 'list'">
                    <!-- 接口 items 结构：加粗小标题 label + 正文 text -->
                    <template v-if="sectionItems(sec).length">
                      <div>
                        <span
                          v-for="(item, li) in sectionItems(sec)"
                          :key="`item-${li}`"
                          class="section-text section-item"
                        >
                          <span v-if="item.label">{{ item.label }}：</span>
                          <span>{{ item.text }}</span>
                        </span>
                      </div>
                    </template>
                    <!-- 旧数据兼容：content 按换行拆分 -->
                    <template v-else>
                      <p v-for="(line, li) in sectionLines(sec)" :key="li" class="section-text">
                        {{ line }}
                      </p>
                    </template>
                  </template>
                  <p v-else class="section-text">{{ sec.content }}</p>
                </section>
              </template>
            </template>

            <template v-else>
              <!-- 题型介绍 -->
              <section v-if="hasIntro" class="guide-section">
                <h3 class="section-title">题型介绍</h3>
                <p class="section-text">{{ guideData.questionIntroduce }}</p>
              </section>

              <!-- 练习建议 -->
              <section v-if="hasSuggest" class="guide-section">
                <h3 class="section-title">练习建议</h3>
                <p class="section-text">{{ guideData.questionSuggest }}</p>
              </section>

              <!-- 讲解视频 -->
              <section v-if="hasVideo" class="guide-section">
                <h3 class="section-title">讲解视频</h3>
                <div class="video-list">
                  <div
                    v-for="(item, index) in videoList"
                    :key="index"
                    class="video-item"
                    v-yt-push="{ btnName: `题型引导-讲解视频-${item.title}` }"
                    @click="openVideo(item.vidioUrl)"
                  >
                    <span class="video-title">{{ item.title }}</span>
                    <img class="play-btn-img" src="/image/home/frequencyPanel/bf.png" alt="播放" />
                  </div>
                </div>
              </section>

              <!-- 练习常见问题 -->
              <section v-if="hasProblem" class="guide-section">
                <h3 class="section-title">练习常见问题</h3>
                <div class="problem-list">
                  <div v-for="(item, index) in problemList" :key="index" class="problem-item">
                    <p class="problem-question">
                      <span class="label">Q:</span>
                      <span>{{ item.question }}</span>
                    </p>
                    <p class="problem-answer">
                      <span class="label">A:</span>
                      <span>{{ item.answer }}</span>
                    </p>
                  </div>
                </div>
              </section>
            </template>
          </template>

          <!-- 加载骨架屏：占位条按真实文案位置摆放 -->
          <el-skeleton v-else class="guide-skeleton" animated :loading="true">
            <template #template>
              <div v-for="i in 3" :key="i" class="sk-section">
                <el-skeleton-item
                  variant="text"
                  class="sk-title"
                  :style="{ width: SK_TITLE_WIDTHS[(i - 1) % SK_TITLE_WIDTHS.length] }"
                />
                <el-skeleton-item
                  v-for="(w, j) in SK_LINE_WIDTHS[(i - 1) % SK_LINE_WIDTHS.length]"
                  :key="`line-${j}`"
                  variant="text"
                  class="sk-line"
                  :style="{ width: w }"
                />
              </div>
            </template>
          </el-skeleton>
        </div>

        <button
          v-if="hasTabs"
          class="confirm-btn"
          v-yt-push="{ btnName: '题型引导-我知道了' }"
          @click="handleClose"
        >
          我知道了
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  getQuestionGuideApi,
  type QuestionGuideData,
  type ModeGuideSection,
  type ModeGuideItem
} from '@/api/questionGuide.api'

export interface GuideTabItem {
  termId: number
  label: string
  typeCode?: string
  /** 题量文案（来自指南配置接口） */
  questionCountText?: string
  /** 命中率文案（来自指南配置接口） */
  hitRateText?: string
  /** MUST_PRACTICE 必刷 / MUST_SEE 必看 */
  priority?: string
}

export interface GuideTabGroup {
  category: string
  list: GuideTabItem[]
}

interface Props {
  modelValue: boolean
  /** 纯展示模式的数据（不传 tabs 时生效） */
  data?: QuestionGuideData
  title?: string
  tabs?: GuideTabGroup[]
  activeTermId?: number | string
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({
    questionIntroduce: '',
    questionSuggest: '',
    explanatoryVideoList: [],
    commonProblemList: []
  }),
  title: '练习指导',
  tabs: () => [],
  activeTermId: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const guideData = ref<QuestionGuideData>({
  questionIntroduce: '',
  questionSuggest: '',
  explanatoryVideoList: [],
  commonProblemList: []
})

const hasTabs = computed(() => Array.isArray(props.tabs) && props.tabs.length > 0)

const tabGroups = computed<GuideTabGroup[]>(() => props.tabs || [])

const currentTermId = ref<number | undefined>(undefined)

/** 已完成加载的 termId：仅当与当前选中项一致时才渲染内容，避免接口未返回前闪现兜底文案 */
const loadedTermId = ref<number | string | undefined>(undefined)

/** 当前选中项的指南数据是否已就绪（纯展示模式数据来自 props，视为始终就绪） */
const contentReady = computed(() => {
  if (!hasTabs.value) return true
  return loadedTermId.value === currentTermId.value
})

const activeItem = computed<GuideTabItem | undefined>(() => {
  if (!currentTermId.value) return undefined
  for (const group of tabGroups.value) {
    const item = group.list.find((i) => i.termId === currentTermId.value)
    if (item) return item
  }
  return undefined
})

const bubbleTitle = computed(() => {
  // 题库综合指南选中时，优先展示第一个模式指南标题（如：列表模式指南）
  if (currentTermId.value === 9001 && modeGuides.value.length) {
    return modeGuides.value[0].title
  }
  // 题库综合指南标题选中时
  if (currentTermId.value === 9001) {
    return '题库综合指南 考试规则技巧：'
  }
  if (!activeItem.value) return ''
  // "其他听力小题"不拼接科目前缀，直接展示：其他听力小题 考试规则技巧：
  if (isOtherRow(activeItem.value)) {
    return `${OTHER_LISTEN_LABEL} 考试规则技巧：`
  }
  const category = tabGroups.value.find((g) =>
    g.list.some((i) => i.termId === currentTermId.value)
  )?.category
  return `${category} ${activeItem.value.label} 考试规则技巧：`
})

const videoList = computed(() => guideData.value.explanatoryVideoList || [])
const problemList = computed(() => guideData.value.commonProblemList || [])
/** 题库综合指南的模式指南（列表模式/速记模式） */
const modeGuides = computed(() => guideData.value.modeGuides || [])

/** 提取模式名（去掉「模式指南」后缀），如「列表模式指南」→「列表」 */
const modeNameOf = (title: string) => title.replace('模式指南', '')

const hasIntro = computed(() => !!guideData.value.questionIntroduce)
const hasSuggest = computed(() => !!guideData.value.questionSuggest)
const hasVideo = computed(() => videoList.value.length > 0)
const hasProblem = computed(() => problemList.value.length > 0)

// 题库总览（题量/稳定期命中率/重要程度）接口暂未提供，先按原型写死假数据，key 为题型 code
const TYPE_STAT_MAP: Record<
  string,
  { count?: string; rate?: string; action?: string; tag?: string }
> = {
  RA: { count: '6~7题', rate: '20%~50%', action: '必刷' },
  RS: { count: '10~12题', rate: '20%~50%', action: '必刷' },
  DI: { count: '5~6题', rate: '30%~50%' },
  RL: { count: '2~3题', rate: '30%~50%' },
  ASQ: { count: '5~6题', rate: '20%~50%' },
  SGD: { count: '2~3题', rate: '近似主题命中率高', action: '必刷' },
  RTS: { count: '2~3题', rate: '近似主题命中率高', action: '必刷' },
  SWT: { count: '2题', rate: '50%', action: '必刷', tag: '必购' },
  WE: { count: '1题', rate: '99%', action: '必刷' },
  FIBR: { count: '5~6题', rate: '50%~75%', action: '必刷' },
  RO: { count: '2~3题', rate: '50%' },
  FIBDD: { count: '4~5题', rate: '50%~75%', action: '必刷' },
  'MCS&MCM': { count: '2~3题' },
  MCS_MCM: { count: '2~3题' },
  SST: { count: '1题', rate: '99%', action: '必刷' },
  FIB: { count: '2~3题' },
  WFD: { count: '3~4题', rate: '99%', action: '必背' },
  HIW: { count: '2~3题' },
  // 其他听力小题
  OTHER_LISTEN: { count: '' }
}

// 行内橙色小标签（展示在"必刷/必看"操作上方），key 为题型 code
const TYPE_TAG_MAP: Record<string, string> = {
  WE: '论点句',
  SST: '关键词&双重练&梳理逻辑'
}

/** "其他听力小题"行的展示文案 */
const OTHER_LISTEN_LABEL = '其他听力小题'

/** 是否为"其他听力小题"行 */
const isOtherRow = (item: GuideTabItem) => {
  return item.typeCode === 'OTHER_LISTEN' || item.label.includes('其他听力')
}

/** 取题型统计：优先使用接口返回的文案，否则回退到写死数据 */
const typeStat = (item: GuideTabItem) => {
  const code = item.typeCode || ''
  // 接口提供了精确文案时直接使用
  if (item.questionCountText || item.hitRateText || item.priority) {
    return {
      count: item.questionCountText || '',
      rate: item.hitRateText || '',
      action:
        item.priority === 'MUST_PRACTICE' ? '必刷' : item.priority === 'MUST_SEE' ? '必看' : '',
      tag: TYPE_TAG_MAP[code] || ''
    }
  }
  const fallback = TYPE_STAT_MAP[code] || {}
  return {
    count: fallback.count || '',
    rate: fallback.rate || '',
    action: fallback.action || '',
    tag: TYPE_TAG_MAP[code] || fallback.tag || ''
  }
}

/** list 类型小节条目：优先使用接口 items（label + text），无 items 时回退到 content 拆行 */
const sectionItems = (sec: ModeGuideSection): ModeGuideItem[] => {
  if (!Array.isArray(sec.items)) return []
  return sec.items.filter((item) => item && (item.label || item.text))
}

/** list 类型小节内容兼容：数组直接使用，字符串按换行拆分 */
const sectionLines = (sec: ModeGuideSection): string[] => {
  if (Array.isArray(sec.content)) return sec.content
  return String(sec.content || '')
    .split('\n')
    .filter((line) => line.trim())
}

/** 骨架屏占位条宽度：标题/正文行按小节轮换，模拟真实内容的参差感 */
const SK_TITLE_WIDTHS = ['128px', '96px', '148px']
const SK_LINE_WIDTHS = [
  ['92%', '86%', '58%'],
  ['88%', '94%', '46%'],
  ['95%', '76%']
]

let reqSeq = 0
const loadGuide = (termId?: number | string) => {
  if (!termId) return
  // 序号守卫：快速切换题型时丢弃过期响应，防止旧数据覆盖新选中项
  const seq = ++reqSeq
  getQuestionGuideApi(termId)
    .then((res) => {
      if (seq !== reqSeq) return
      guideData.value = res.data
      loadedTermId.value = termId
    })
    .catch(() => {
      // 请求失败时也标记就绪，回退到兜底标题/空内容，避免永久空白
      if (seq === reqSeq) loadedTermId.value = termId
    })
}

const getDefaultTermId = () => {
  if (!hasTabs.value) return undefined
  if (props.activeTermId) return Number(props.activeTermId)
  // 默认选中题库综合指南（termId=9001）
  return 9001
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      if (hasTabs.value) {
        const targetId = getDefaultTermId()
        if (targetId) {
          currentTermId.value = targetId
          loadGuide(targetId)
        }
      } else {
        guideData.value = props.data || {
          questionIntroduce: '',
          questionSuggest: '',
          explanatoryVideoList: [],
          commonProblemList: []
        }
      }
    } else {
      guideData.value = {
        questionIntroduce: '',
        questionSuggest: '',
        explanatoryVideoList: [],
        commonProblemList: []
      }
      loadedTermId.value = undefined
    }
  },
  { immediate: true }
)

watch(
  () => props.activeTermId,
  (val) => {
    if (!hasTabs.value || !val) return
    const termId = Number(val)
    if (termId === currentTermId.value) return
    currentTermId.value = termId
    if (visible.value) {
      loadGuide(termId)
    }
  },
  { immediate: true }
)

const switchType = (item: GuideTabItem) => {
  if (item.termId === currentTermId.value) return
  currentTermId.value = item.termId
  loadGuide(item.termId)
}

const switchGuideTitle = () => {
  if (currentTermId.value === 9001) return
  currentTermId.value = 9001
  loadGuide(9001)
}

const handleClose = () => {
  visible.value = false
}

// ==================== 左侧列表滚动定位 ====================

const tableBodyRef = ref<HTMLElement>()

/** 左侧列表滚动到当前选中项：选中行不在可视范围内时居中滚动（深链打开时选中项可能在列表下方） */
const scrollActiveIntoView = () => {
  nextTick(() => {
    const container = tableBodyRef.value
    if (!container || !currentTermId.value) return
    const el = container.querySelector<HTMLElement>(`[data-term-id="${currentTermId.value}"]`)
    if (!el) return
    const cRect = container.getBoundingClientRect()
    const rRect = el.getBoundingClientRect()
    // 已完整在可视范围内时不滚动，避免打断用户手动滚动
    if (rRect.top >= cRect.top && rRect.bottom <= cRect.bottom) return
    container.scrollTop += rRect.top - cRect.top - (cRect.height - rRect.height) / 2
  })
}

// 弹窗打开（含 destroy-on-close 重建 DOM）/ 深链切换选中项 / 左表数据就绪时，重新定位选中行
watch([visible, currentTermId, () => tabGroups.value.length], ([val]) => {
  if (val && hasTabs.value) scrollActiveIntoView()
})

const openVideo = (url: string) => {
  if (!url) return
  window.open(url, '_blank')
}
</script>

<style lang="scss" scoped>
:global(.el-dialog.question-guide-dialog) {
  background: linear-gradient(180deg, #e7f7fc 0%, #fdf8e3 100%);
  border-radius: 24px;
  // 放开裁剪，允许关闭按钮悬浮在弹窗边角外侧；内部溢出裁剪由 .guide-layout 承担
  overflow: visible;
  box-shadow: none;
}

:global(.el-dialog.question-guide-dialog .el-dialog__header) {
  padding: 0;
  margin: 0;
  border-bottom: none;
  background: transparent;
}

:global(.el-dialog.question-guide-dialog .el-dialog__body) {
  background: transparent;
  padding: 0;
}

:global(.el-dialog.question-guide-dialog .el-dialog__footer) {
  padding: 0;
  background: transparent;
}

.dialog-title {
  display: inline-block;
  padding: 20px 0 0 20px;
  font-size: var(--el-font-size-large);
  font-weight: 600;
  color: #333;
}

/* ===== tabs 模式双栏布局 ===== */
.guide-layout {
  position: relative;
  display: flex;
  gap: 20px;
  height: min(620px, 76vh);
  // 不裁剪：标题气泡（top:-14px）与眼睛图标（top:-9px）向上探入弹窗顶部内边距区域，需完整显示
  //（若用 overflow: hidden 会把两者的顶部切平，原 el-dialog 的裁剪行为即为此 bug 来源）
  overflow: visible;
}

/* 左侧：题库综合指南 */
.type-panel {
  position: relative;
  flex-shrink: 0;
  width: 340px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  padding: 16px 14px 10px;

  .panel-title {
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    color: #333;
    margin-bottom: 10px;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 10px;
    transition: all 0.2s;
    background: #f6f6f9;
    border-radius: 8px;
    margin-left: 27px;

    &:hover {
      background: #f0f0f5;
    }

    &.active {
      background: linear-gradient(90deg, #f6fab1 0%, #aaede6 26.22%, #e4f7bd 100%);
      color: #222;
    }

    .arrow {
      display: inline-block;
      color: #999;
      font-weight: 400;
      margin-left: 2px;
      font-size: 24px;
      line-height: 1;
      vertical-align: -2px;
    }
  }

  .new-badge {
    position: absolute;
    top: 10px;
    right: 12px;
    background: #f68484;
    color: #fff;
    font-size: 10px;
    line-height: 1;
    padding: 4px 7px;
    border-radius: 6px;
  }

  .table-head {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #9a9caa;
    // 左内边距对齐行内容（22px 科目标签 + 10px 行内边距），保证"题型"表头与题型列中心对齐
    padding: 0 10px 8px 32px;

    .col-type {
      width: 78px;
      text-align: center;
      flex-shrink: 0;
    }

    .col-info {
      flex: 1;
      text-align: center;
    }
  }

  .table-body {
    flex: 1;
    overflow-y: auto;
    // 隐藏滚动条（滚动条占据行内容右侧空间导致行未铺满面板），悬浮滚轮滚动仍可用
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    display: flex;
    flex-direction: column;

    .type-group {
      display: flex;
      align-items: stretch;
      margin-bottom: 8px;

      // 听/说/读/写分组之间的分隔线（左起与题型行对齐，不覆盖科目标签列）
      & + .type-group {
        position: relative;
        padding-top: 12px;
        margin-top: 4px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 35px;
          right: 0;
          border-top: 1px solid #f4f4f4;
        }
      }

      &:last-child {
        margin-bottom: 0;
      }

      .group-label {
        flex-shrink: 0;
        width: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        color: #636574;
        writing-mode: vertical-rl;
        text-orientation: upright;
        letter-spacing: 4px;
      }

      .group-rows {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
      }
    }

    .type-row {
      position: relative;
      display: flex;
      align-items: center;
      min-height: 36px;
      border-radius: 10px;
      padding: 3px 0 3px 10px;
      font-size: 12px;
      color: #4a4a6a;
      cursor: pointer;
      transition: all 0.2s;
      // 行内所有文字不换行，超出省略
      white-space: nowrap;

      // 未选中：题型与题量分属两个独立背景块，中间留缝；选中后渐变背景连为一体
      .t-name {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        align-self: stretch;
        // 宽度需容纳最长题型名（MCS&MCM），保证各行列文字对齐
        width: 72px;
        flex-shrink: 0;
        padding: 0 4px;
        background: #f6f6f9;
        border-radius: 8px;
        overflow: hidden;
      }

      .t-info {
        position: relative;
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        align-self: stretch;
        margin-left: 6px;
        padding: 0 8px;
        background: #f6f6f9;
        border-radius: 8px;
        color: #9a9caa;

        .row-action {
          flex-shrink: 0;
          margin-left: 6px;
          font-style: normal;
          font-size: 12px;
          color: #008cff;
          white-space: nowrap;
        }
      }

      &:hover:not(.active) .t-name,
      &:hover:not(.active) .t-info {
        background: #eef0f8;
      }

      &.active {
        color: #222;

        // 渐变背景用伪元素绘制：边缘与灰色块外边缘对齐（不铺满行内边距），中间缝隙连为一体
        &::before {
          content: '';
          position: absolute;
          inset: 3px 0 3px 10px;
          background: linear-gradient(90deg, #f6fab1 0%, #aaede6 26.22%, #e4f7bd 100%);
          border-radius: 8px;
        }

        // 灰色块背景透明，让伪元素渐变透出
        .t-name,
        .t-info {
          background: transparent;
        }
      }

      .t-count {
        width: 56px;
        flex-shrink: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
      }

      .t-rate {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-left: 2px;
        text-align: left;
      }

      // 橙色小标签：悬浮于行右上角（"必刷"上方）
      .row-tag {
        position: absolute;
        top: -7px;
        right: 8px;
        font-style: normal;
        font-size: 10px;
        line-height: 1;
        color: #ff8558;
        white-space: nowrap;
        background: #ffece4;
        padding: 3px 4px;
        border-radius: 6px;
      }

      // 其他听力小题：整行居中展示
      &.other-row {
        justify-content: center;

        .other-text {
          position: relative;
          color: #9a9caa;
          width: 100%;
          height: 100%;
          // 撑满行高，与其余题型行的灰色块及选中渐变区域对齐
          align-self: stretch;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #f6f6f9;
          border-radius: 8px;
        }

        // 选中态：文字块背景透明，让行上绘制的渐变伪元素透出，文字同步加深
        &.active {
          color: #222;

          .other-text {
            color: #222;
            background: transparent;
          }
        }
      }
    }
  }
}

/* 右上角关闭按钮：灰底圆形 + 白色叉叉，骑在弹窗右上角边角上（样式对齐周年庆弹窗，相对于 .el-dialog 定位） */
.dialog-close-btn {
  position: absolute;
  top: -19px;
  right: -42px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #b6b6b6;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.08);
  }
}

/* 右侧：考试规则技巧 */
.guide-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  padding: 0 20px 16px;

  .guide-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
    padding: 14px 0 4px;
    position: relative;

    // 标题气泡：背景随内容尺寸拉伸，贴住文字
    .title-bubble {
      max-width: calc(100% - 70px);
      padding: 5px 16px 8px;
      background: url('/image/home/frequencyPanel/Vector.png') no-repeat center / 100% 100%;
      height: 70px;
      position: absolute;
      top: -14px;
      left: -24px;
      display: flex;
      justify-content: center;
      align-items: center;
      .title-text {
        display: block;
        color: #323553;
        font-size: 20px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transform: rotate(-4deg);
      }
      // 模式名（如「列表」/「速记」）高亮色，后缀「模式指南」保持原色
      .mode-name {
        color: #00bbff;
      }
    }

    .eye-icon {
      flex-shrink: 0;
      width: 52px;
      height: 52px;
      object-fit: contain;
      pointer-events: none;
      position: absolute;
      right: 34px;
      top: -9px;
      transform: rotate(-5deg);
    }
  }

  // 内容流中的模式标题气泡（第二个及之后的模式指南，如：速记模式指南），靠右展示
  .mode-bubble-row {
    display: flex;
    justify-content: flex-end;
    padding: 40px 6% 10px 0;
    position: relative;

    .mode-bubble {
      transform: rotate(24deg);
      max-width: calc(100% - 70px);
      padding: 5px 16px 8px;
      background: url('/image/home/frequencyPanel/Vector.png') no-repeat center / 100% 100%;
      height: 70px;
      position: absolute;
      top: -2px;
      display: flex;
      justify-content: center;
      align-items: center;
      .mode-text {
        display: block;
        color: #323553;
        font-size: 20px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        // 与顶部标题气泡保持一致的文字歪度，保证字体与气泡背景图形倾斜吻合
        transform: rotate(-9deg);
        // 模式名（如「速记」）高亮色，后缀「模式指南」保持原色
        .mode-name {
          color: #00bbff;
        }
      }
    }

    // NEW 角标：叠在气泡右上角
    .mode-badge {
      position: absolute;
      top: -3px;
      right: 10px;
      font-size: 12px;
      color: #fff;
      background: #f68484;
      padding: 1px 5px;
      border-radius: 6px;
      transform: rotate(-1deg);
      box-shadow: 0 2px 6px rgba(255, 91, 77, 0.3);
    }
  }

  .guide-content {
    flex: 1;
    overflow-y: auto;
    // 隐藏滚动条，悬浮滚轮滚动仍可用
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    margin-top: 35px;
  }

  // 加载骨架屏：占位条按真实内容结构（小节标题 + 正文行）摆放
  .guide-skeleton {
    display: block;
    padding: 10px 4px;

    .sk-section {
      margin-bottom: 28px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .sk-title {
      display: block;
      height: 18px;
      margin-bottom: 6px;
    }

    .sk-line {
      display: block;
      height: 14px;
      margin-top: 10px;
    }
  }

  .confirm-btn {
    flex-shrink: 0;
    width: 100%;
    height: 46px;
    margin-top: 12px;
    border: none;
    border-radius: 24px;
    font-size: var(--el-font-size-medium);
    font-weight: 700;
    color: #333;
    cursor: pointer;
    background: linear-gradient(270deg, #a3eceb 0%, #f8faae 100%);
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }
}

/* 纯展示模式（无 tabs）沿用单栏 */
.guide-layout > .guide-panel:only-child {
  padding-top: 12px;
}

.guide-section {
  border-radius: 16px;
  padding: 10px 4px;
}

.section-title {
  position: relative;
  display: inline-block;
  padding-left: 16px;
  font-size: var(--el-font-size-large);
  font-weight: 700;
  color: #333;
  margin: 0 0 4px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background: #6aa1ff;
    border-radius: 2px;
  }
}

.section-text {
  position: relative;
  font-size: var(--el-font-size-base);
  line-height: 24px;
  color: #9a9caa;
  margin: 0;
}

// list 类型条目：加粗小标题 + 正文，条目间留间距
.section-item {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  .item-label {
    font-weight: 700;
    color: #333;
    margin-right: 2px;
  }
}

.video-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8f8f8 url('/image/home/frequencyPanel/video.png') no-repeat right bottom / auto 80%;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .video-title {
    font-size: var(--el-font-size-base);
    font-weight: 600;
    color: #333;
  }

  .play-btn-img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
}

.problem-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.problem-item {
  .problem-question {
    font-size: var(--el-font-size-base);
    font-weight: 600;
    color: #333;
    line-height: 22px;
    margin: 0 0 8px;
  }

  .problem-answer {
    font-size: var(--el-font-size-base);
    color: #666;
    line-height: 22px;
    margin: 0;
  }

  .label {
    margin-right: 6px;
    font-weight: 600;
  }
}
</style>
